import { Trans, useI18n } from '@affine/i18n';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import clsx from 'clsx';
import { type ReactNode, useCallback } from 'react';

import {
  BrandLoader,
  BrandWordmark,
  STREAMLAIN_BRAND_LOCKUP,
} from '../../brand';
import { Button } from '../../ui/button';
import * as styles from './index.css';

const ReportProblemLink = ({
  children,
  onReportProblem,
}: {
  children?: ReactNode;
  onReportProblem: () => void;
}) => (
  <>
    <br />
    <button
      type="button"
      className={styles.reportProblemLink}
      onClick={onReportProblem}
    >
      {children}
    </button>
  </>
);

export const LoadingScreenLogo = ({
  animated = false,
}: {
  animated?: boolean;
}) => {
  return (
    <div className={styles.loadingBrand}>
      <BrandLoader
        size={160}
        animated={animated}
        className={styles.loadingLogo}
        aria-label={BRAND_DISPLAY_NAME}
      />
      <BrandWordmark
        size={STREAMLAIN_BRAND_LOCKUP.wordmarkWidth}
        className={styles.loadingWordmark}
        aria-label={`${BRAND_DISPLAY_NAME} wordmark`}
      />
    </div>
  );
};

export interface LoadingSplashProgress {
  label: string;
  detail?: string;
  value?: number;
  mode?: 'progress' | 'indeterminate';
}

const LoadingProgress = ({ progress }: { progress: LoadingSplashProgress }) => {
  const progressValue =
    progress.value === undefined
      ? undefined
      : Math.min(1, Math.max(0, progress.value));
  const progressMode =
    progress.mode ??
    (progressValue === undefined ? 'indeterminate' : 'progress');
  return (
    <div className={styles.loadingProgress} data-progress-mode={progressMode}>
      <div className={styles.loadingProgressLabel}>{progress.label}</div>
      {progress.detail ? (
        <div className={styles.loadingProgressDetail}>{progress.detail}</div>
      ) : null}
      <div
        className={styles.loadingProgressTrack}
        role="progressbar"
        aria-label={progress.label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={
          progressValue === undefined
            ? undefined
            : Math.round(progressValue * 100)
        }
      >
        <div
          className={styles.loadingProgressFill}
          style={
            progressValue === undefined
              ? undefined
              : { width: `${Math.round(progressValue * 100)}%` }
          }
        />
      </div>
    </div>
  );
};

export const LoadingSplashForeground = ({
  label,
  progress,
}: {
  label?: string;
  progress?: LoadingSplashProgress;
}) => {
  return (
    <div
      className={styles.loadingSplashForeground}
      role={label ? 'status' : undefined}
      aria-label={label}
      data-testid="loading-splash-foreground"
    >
      <LoadingScreenLogo animated />
      {progress ? <LoadingProgress progress={progress} /> : null}
    </div>
  );
};

/**
 * Shared brand screen: the StreamLain logo lockup on the theme-bound surface,
 * with a slot for the bottom block. Reused by the doc loading state and the
 * not-found/no-access pages so transitions between them cause no layout shift.
 */
export const BrandScreen = ({
  animated = false,
  children,
  fullBleed = false,
  label,
  progress,
}: {
  animated?: boolean;
  children?: ReactNode;
  fullBleed?: boolean;
  label?: string;
  progress?: LoadingSplashProgress;
}) => {
  return (
    <div
      className={clsx(
        styles.blockSuiteEditorStyle,
        fullBleed && styles.brandScreenFullBleed
      )}
      role={label ? 'status' : undefined}
      aria-label={label}
      data-testid="brand-screen"
    >
      <div className={styles.brandAnchor}>
        <LoadingScreenLogo animated={animated} />
        <div className={styles.brandSlot}>
          {progress ? <LoadingProgress progress={progress} /> : null}
          {children}
        </div>
      </div>
    </div>
  );
};

export const EditorLoading = ({
  animated = true,
  longerLoading = false,
  onReportProblem,
}: {
  longerLoading?: boolean;
  animated?: boolean;
  /** Opens "Report a bug" with the diagnostics of this load. */
  onReportProblem?: () => void;
}) => {
  const showNotice = longerLoading;
  const t = useI18n();
  const reloadPage = useCallback(() => {
    document.location.reload();
  }, []);
  return (
    <BrandScreen animated={animated && !showNotice}>
      {showNotice ? (
        <div className={styles.content} data-longer-loading={true}>
          <div>
            <div className={styles.text} data-longer-loading={true}>
              {t['com.streamlain.error.loading-timeout-error']()}
            </div>
            {onReportProblem ? (
              <div className={styles.text} data-longer-loading={true}>
                <Trans
                  i18nKey="com.streamlain.error.loading-timeout-report"
                  components={{
                    1: <ReportProblemLink onReportProblem={onReportProblem} />,
                  }}
                />
              </div>
            ) : null}
          </div>
          <Button
            size="large"
            className={clsx(
              BUILD_CONFIG.isMobileEdition
                ? styles.mobileActionButton
                : styles.actionButton
            )}
            contentClassName={clsx(
              BUILD_CONFIG.isMobileEdition
                ? styles.mobileActionContent
                : styles.actionContent
            )}
            onClick={reloadPage}
            variant="primary"
          >
            {t['com.streamlain.error.reload']()}
          </Button>
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.title}>{t['com.streamlain.loading']()}</div>
          <div className={styles.text}>
            {t['com.streamlain.loading.description']()}
          </div>
        </div>
      )}
    </BrandScreen>
  );
};

export const PageDetailLoading = () => {
  return (
    <div className={styles.pageDetailSkeletonStyle}>
      <EditorLoading />
    </div>
  );
};
